import { useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ClipboardList,
  History,
  Lock,
  Printer,
  Store,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { registerKey, usePos, useRegister, useStaff } from "@/app/context";
import { BigButton } from "@/components/BigButton";
import { MoneyText, useYen } from "@/components/MoneyText";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useCart } from "@/features/sale/hooks/useCart";
import { call } from "@/lib/api";
import { readSetting } from "@/lib/db";
import { errorText, isApiError, type ApiError } from "@/lib/errors";

import { CashMovementModal, type MovementType } from "./CashMovementModal";
import { CloseResult } from "./CloseResult";
import { DenominationCounter } from "./DenominationCounter";
import { countTotal, toPayload, type Counts } from "./denominations";
import { readReport, type ReportData } from "./report";
import { ReportView } from "./ReportView";
import { ShiftHistory } from "./ShiftHistory";
import { currentShiftKey, printShiftReport, useCurrentShift } from "./useShift";

type Mode = "home" | "close" | "history";

interface Result {
  shiftId: string;
  report: ReportData;
}

interface Waiting {
  shiftId: string | null;
  name: string | null;
  at: string | null;
}

function hhmm(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  return new Intl.DateTimeFormat("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  }).format(new Date(value));
}

/**
 * レジ開閉 (T07-26, design/05 §3.7): open the till by counting the change money note by note,
 * 入金 / 出金 with a reason, 途中集計 (X), and close: count → the server compares with what
 * should be in the drawer → 過不足 in colour (the manager's PIN when it is too large) → Z report
 * printed / PDF. Everything is in the staff member's language; the amounts are the server's.
 */
export function ShiftScreen() {
  const { t } = useTranslation();
  const register = useRegister();
  const [result, setResult] = useState<Result | null>(null);
  const [mode, setMode] = useState<Mode>("home");
  const [closing, setClosing] = useState<string | null>(null);
  const openShift = register.data?.openShift ?? null;

  if (result) {
    return (
      <CloseResult
        shiftId={result.shiftId}
        report={result.report}
        threshold={register.data?.rules.shiftVarianceManagerThreshold ?? 1000}
        onDone={() => {
          setResult(null);
          setMode("home");
          setClosing(null);
        }}
      />
    );
  }
  if (mode === "history") {
    return <ShiftHistory onBack={() => setMode("home")} />;
  }
  const closeId = closing ?? (mode === "close" ? (openShift?.shiftId ?? null) : null);
  if (closeId) {
    return (
      <CloseShift
        shiftId={closeId}
        onCancel={() => {
          setClosing(null);
          setMode("home");
        }}
        onClosed={(report) => {
          setResult({ shiftId: closeId, report });
          setClosing(null);
        }}
      />
    );
  }
  if (!openShift) {
    return (
      <OpenShift
        onHistory={() => setMode("history")}
        onClosePrevious={(shiftId) => setClosing(shiftId)}
      />
    );
  }
  return (
    <OpenHome
      shiftId={openShift.shiftId}
      openedByName={openShift.openedByName ?? null}
      openedAt={openShift.openedAt}
      onClose={() => setMode("close")}
      onHistory={() => setMode("history")}
      title={t("shift.title")}
    />
  );
}

function useHideTwoThousand(): boolean {
  const { db } = usePos();
  return useLiveQuery(() => readSetting(db, "hideTwoThousand", false), [db]) ?? false;
}

// ---- open -----------------------------------------------------------------------------------

function OpenShift({
  onHistory,
  onClosePrevious,
}: {
  onHistory: () => void;
  onClosePrevious: (shiftId: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = usePos();
  const register = useRegister();
  const staff = useStaff();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const hide2000 = useHideTwoThousand();
  const [counts, setCounts] = useState<Counts>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<Waiting | null>(null);
  const total = countTotal(counts);

  const open = async () => {
    if (!register.data || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await call(() =>
        api.POST("/v1/pos/shifts/open", {
          body: {
            registerId: register.data.register.id,
            openingFloat: total,
            denominations: toPayload(counts),
            ...(staff?.name ? { openedByName: staff.name } : {}),
          },
        }),
      );
      await queryClient.invalidateQueries({ queryKey: registerKey });
      void navigate("/", { replace: true });
    } catch (err) {
      if (isApiError(err, "shift_already_open")) {
        const detail = (err as ApiError).detail;
        setWaiting({
          shiftId: typeof detail.shiftId === "string" ? detail.shiftId : null,
          name: typeof detail.openedByName === "string" ? detail.openedByName : null,
          at: typeof detail.openedAt === "string" ? detail.openedAt : null,
        });
        await queryClient.invalidateQueries({ queryKey: registerKey });
        return;
      }
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const closePrevious = () => {
    if (!waiting?.shiftId) {
      return;
    }
    // pos-service checks it at the close itself (someone else's shift: pos.shift.manage or the
    // manager's one-use approval, action shift.close_previous) — the approval is asked for when
    // the count is sent, so it cannot run out (2 minutes) while the drawer is being counted.
    onClosePrevious(waiting.shiftId);
  };

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr] gap-3 p-4">
      <header className="flex items-center gap-3">
        <Store aria-hidden className="size-9" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{t("shift.required_title")}</h1>
          <p className="text-muted-foreground">{t("shift.required_body")}</p>
        </div>
        <BigButton variant="outline" onClick={onHistory}>
          <History aria-hidden />
          {t("shift.history")}
        </BigButton>
      </header>
      {waiting ? (
        <Modal
          open
          title={t("shift.already_open_title")}
          description={t("shift.already_open_body", {
            name: waiting.name ?? t("shift.someone"),
            time: hhmm(waiting.at),
          })}
          onClose={() => setWaiting(null)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setWaiting(null)}
                className="min-h-pos-touch rounded-md border px-6 text-lg"
              >
                {t("app.cancel")}
              </button>
              <button
                type="button"
                onClick={closePrevious}
                disabled={!waiting.shiftId}
                className="min-h-pos-touch rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground"
              >
                {t("shift.close_previous")}
              </button>
            </>
          }
        >
          <p>{t("shift.close_previous_help")}</p>
        </Modal>
      ) : null}
      <DenominationCounter
        counts={counts}
        onChange={setCounts}
        hideTwoThousand={hide2000}
        disabled={busy || !register.data}
        label={t("shift.float_label")}
      >
        {error ? (
          <p role="alert" className="text-pos-problem">
            {error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => void open()}
          disabled={busy || !register.data}
          className="min-h-pos-touch rounded-md bg-primary text-xl font-semibold text-primary-foreground disabled:opacity-50"
        >
          {busy ? t("app.saving") : t("shift.open_submit")}
        </button>
      </DenominationCounter>
    </div>
  );
}

// ---- open shift: home -----------------------------------------------------------------------

function OpenHome({
  shiftId,
  openedByName,
  openedAt,
  onClose,
  onHistory,
  title,
}: {
  shiftId: string;
  openedByName: string | null;
  openedAt: string;
  onClose: () => void;
  onHistory: () => void;
  title: string;
}) {
  const { t } = useTranslation();
  const yen = useYen();
  const { api, printer } = usePos();
  const toast = useToast();
  const queryClient = useQueryClient();
  const current = useCurrentShift(shiftId);
  const [movement, setMovement] = useState<MovementType | null>(null);
  const [xReport, setXReport] = useState<ReportData | null>(null);
  const [xBusy, setXBusy] = useState(false);
  const summary = current.data?.summary;

  const showX = async () => {
    setXBusy(true);
    try {
      const answer = await call(() =>
        api.GET("/v1/pos/shifts/{shift_id}/x-report", {
          params: { path: { shift_id: shiftId } },
        }),
      );
      if ("report" in answer) {
        setXReport(readReport(answer.report));
      }
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setXBusy(false);
    }
  };

  const printX = async () => {
    const ok = await printShiftReport({ api, printer, shiftId, kind: "x" });
    toast.show(ok ? t("shift.printed") : t("shift.print_failed"), ok ? "success" : "error");
  };

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr] gap-4 p-4">
      <header className="flex flex-wrap items-center gap-4">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-lg" data-testid="shift-status">
          {t("shift.opened_by", { name: openedByName ?? t("shift.someone"), time: hhmm(openedAt) })}
        </p>
      </header>
      <div className="grid min-h-0 grid-cols-[minmax(0,1fr)_22rem] gap-4">
        <section aria-label={t("shift.actions")} className="grid grid-cols-2 content-start gap-3">
          <BigButton variant="outline" block onClick={() => setMovement("pay_in")}>
            <ArrowDownToLine aria-hidden />
            {t("shift.pay_in")}
          </BigButton>
          <BigButton variant="outline" block onClick={() => setMovement("pay_out")}>
            <ArrowUpFromLine aria-hidden />
            {t("shift.pay_out")}
          </BigButton>
          <BigButton variant="outline" block onClick={() => void showX()} disabled={xBusy}>
            <ClipboardList aria-hidden />
            {t("shift.x_report")}
          </BigButton>
          <BigButton variant="outline" block onClick={onHistory}>
            <History aria-hidden />
            {t("shift.history")}
          </BigButton>
          <BigButton block className="col-span-2 min-h-24 text-2xl" onClick={onClose}>
            <Lock aria-hidden />
            {t("shift.close")}
          </BigButton>
        </section>
        <aside aria-label={t("shift.now")} className="flex flex-col gap-3 rounded-md border p-4">
          <p className="text-sm text-muted-foreground">{t("shift.expected_now")}</p>
          {summary ? (
            <MoneyText amount={summary.expectedCash} size="xl" data-testid="shift-expected" />
          ) : (
            // not "¥0" while the drawer total is still loading (someone would count against it)
            <p className="text-2xl text-muted-foreground" data-testid="shift-expected" aria-busy>
              {t("shift.loading_total")}
            </p>
          )}
          <p>{t("shift.sales_count", { count: summary?.salesCount ?? 0 })}</p>
          <p>{t("shift.returns_count", { count: summary?.returnsCount ?? 0 })}</p>
          <p>
            {t("shift.net")}: {yen(summary?.net ?? 0)}
          </p>
          <ul className="flex flex-col gap-1 text-sm">
            {(summary?.tenders ?? []).map((tender) => (
              <li key={tender.method} className="flex justify-between">
                <span>
                  {t(`report.tender.${tender.method}`, {
                    defaultValue: tender.labelJa ?? tender.method,
                  })}
                </span>
                <MoneyText amount={tender.net} size="sm" />
              </li>
            ))}
          </ul>
        </aside>
      </div>
      {movement ? (
        <CashMovementModal
          type={movement}
          shiftId={shiftId}
          onClose={() => setMovement(null)}
          onDone={(done) => {
            setMovement(null);
            toast.show(
              t(done.type === "pay_out" ? "shift.pay_out_done" : "shift.pay_in_done", {
                amount: yen(done.amount),
                drawer: yen(done.expectedCashAfter ?? 0),
              }),
            );
            void queryClient.invalidateQueries({ queryKey: currentShiftKey(shiftId) });
          }}
        />
      ) : null}
      {xReport ? (
        <Modal
          open
          size="lg"
          title={t("report.title_x")}
          description={t("shift.x_help")}
          onClose={() => setXReport(null)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setXReport(null)}
                className="min-h-pos-touch rounded-md border px-6 text-lg"
              >
                {t("app.close")}
              </button>
              <button
                type="button"
                onClick={() => void printX()}
                className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground"
              >
                <Printer aria-hidden />
                {t("shift.print_x")}
              </button>
            </>
          }
        >
          <ReportView report={xReport} />
        </Modal>
      ) : null}
    </div>
  );
}

// ---- close ----------------------------------------------------------------------------------

function CloseShift({
  shiftId,
  onCancel,
  onClosed,
}: {
  shiftId: string;
  onCancel: () => void;
  onClosed: (report: ReportData) => void;
}) {
  const { t } = useTranslation();
  const { api } = usePos();
  const staff = useStaff();
  const cart = useCart();
  const queryClient = useQueryClient();
  const { withApproval } = useApproval();
  const hide2000 = useHideTwoThousand();
  const [counts, setCounts] = useState<Counts>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string[] | null>(null);
  const basketOpen =
    Boolean(cart.cart) && cart.cart?.status === "open" && (cart.cart?.lines.length ?? 0) > 0;
  const guardIds = blocked ?? (basketOpen && cart.cartId ? [cart.cartId] : []);

  const submit = async () => {
    if (busy || guardIds.length > 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const answer = await withApproval(
        (token) =>
          call(() =>
            api.POST("/v1/pos/shifts/{shift_id}/close", {
              params: { path: { shift_id: shiftId } },
              body: {
                denominations: toPayload(counts),
                countedCash: countTotal(counts),
                ...(staff?.name ? { closedByName: staff.name } : {}),
              },
              headers: token ? { "X-Approval-Token": token } : {},
            }),
          ),
        { subject: shiftId },
      );
      await queryClient.invalidateQueries({ queryKey: registerKey });
      onClosed(readReport(answer.report));
    } catch (err) {
      if (err instanceof ApprovalCancelled) {
        setError(t("shift.close_needs_manager"));
      } else if (isApiError(err, "shift_has_open_holds")) {
        const ids = (err as ApiError).detail.cartIds;
        setBlocked(Array.isArray(ids) ? ids.map(String) : []);
      } else {
        setError(errorText(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_1fr] gap-3 p-4">
      <header className="flex items-center gap-3">
        <Lock aria-hidden className="size-8" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{t("shift.close_title")}</h1>
          <p className="text-muted-foreground">{t("shift.close_body")}</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="min-h-pos-touch rounded-md border px-5 text-lg"
        >
          {t("app.back")}
        </button>
      </header>
      <DenominationCounter
        counts={counts}
        onChange={setCounts}
        hideTwoThousand={hide2000}
        disabled={busy}
        label={t("shift.counted")}
      >
        {error ? (
          <p role="alert" className="text-pos-problem">
            {error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="min-h-pos-touch rounded-md bg-primary text-xl font-semibold text-primary-foreground disabled:opacity-50"
        >
          {busy ? t("app.saving") : t("shift.close_submit")}
        </button>
      </DenominationCounter>
      {guardIds.length > 0 ? (
        <OpenBasketsDialog
          cartIds={guardIds}
          onCancel={onCancel}
          onResolved={() => setBlocked(null)}
        />
      ) : null}
    </div>
  );
}

/** Baskets still open on this register: park (保留) or throw away (破棄) before the close. */
function OpenBasketsDialog({
  cartIds,
  onCancel,
  onResolved,
}: {
  cartIds: string[];
  onCancel: () => void;
  onResolved: () => void;
}) {
  const { t } = useTranslation();
  const { api } = usePos();
  const cart = useCart();
  const toast = useToast();
  const [done, setDone] = useState<ReadonlySet<string>>(() => new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const left = cartIds.filter((id) => !done.has(id));

  const act = async (cartId: string, how: "park" | "discard") => {
    setBusy(cartId);
    try {
      if (how === "park") {
        await call(() =>
          api.POST("/v1/pos/carts/{cart_id}/hold", {
            params: { path: { cart_id: cartId } },
            body: { kind: "park" },
          }),
        );
      } else {
        await call(() =>
          api.DELETE("/v1/pos/carts/{cart_id}", { params: { path: { cart_id: cartId } } }),
        );
      }
      if (cartId === cart.cartId) {
        await cart.startNew();
      }
      const next = new Set(done).add(cartId);
      setDone(next);
      if (cartIds.every((id) => next.has(id))) {
        onResolved();
      }
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      open
      title={t("shift.baskets_title")}
      description={t("shift.baskets_body")}
      onClose={onCancel}
      busy={busy !== null}
    >
      <ul className="flex flex-col gap-3">
        {left.map((id) => {
          const mine = id === cart.cartId ? cart.cart : undefined;
          return (
            <li key={id} className="flex flex-col gap-2 rounded-md border p-3">
              <p className="text-lg">
                {mine
                  ? t("shift.basket_line", { count: mine.totals.itemCount })
                  : t("shift.basket_other")}
                {mine ? <MoneyText amount={mine.totals.total} className="ml-2" /> : null}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void act(id, "park")}
                  className="min-h-pos-touch rounded-md border text-lg"
                >
                  {t("shift.basket_park")}
                </button>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void act(id, "discard")}
                  className="min-h-pos-touch rounded-md border border-status-problem text-lg text-pos-problem"
                >
                  {t("shift.basket_discard")}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
